<template>
    <vx-expandable-card>
        <template v-slot:header> <slot name="header"></slot> </template>
        <template v-slot:headerRight> <slot name="headerRight"></slot> </template>
        <template v-slot:content>
            <div class="flex flex-col gap-4 mt-3">
                <slot name="alerts"></slot>
                <slot name="card"></slot>
                <slot v-if="props.showAddressFields" name="address">
                    <div>
                        <vx-label size="md" class="w-full">
                            <div class="flex items-center justify-between grow">
                                <span>Company Legal Name</span>
                                <vx-badge color="slate" size="xs">
                                    <span class="pb-[1.5px] text-xs leading-snug">Optional</span>
                                    <font-awesome-icon widthAuto :icon="faPencilSlash" size="xs" />
                                </vx-badge>
                            </div>
                        </vx-label>

                        <vx-input
                            data-test="company-legal-name-input"
                            v-model="form.company_legal_name"
                            size="lg"
                            :error="displayFirstError(validate.company_legal_name.$errors)"
                            :disabled="props.readOnly"
                        ></vx-input>
                    </div>

                    <div>
                        <vx-label size="md">Address</vx-label>

                        <vx-input
                            data-test="address-1-input"
                            v-model="form.address"
                            size="lg"
                            :error="displayFirstError(validate.address.$errors)"
                            :disabled="props.readOnly"
                        ></vx-input>
                    </div>

                    <div>
                        <vx-label size="md" class="w-full">
                            <div class="flex items-center justify-between grow">
                                <span>Address Line 2</span>
                                <vx-badge color="slate" size="xs">
                                    <span class="pb-[1.5px] text-xs leading-snug">Optional</span>
                                    <font-awesome-icon widthAuto :icon="faPencilSlash" size="xs" />
                                </vx-badge>
                            </div>
                        </vx-label>

                        <vx-input
                            data-test="address-2-input"
                            v-model="form.address_line_2"
                            size="lg"
                            :error="displayFirstError(validate.address_line_2.$errors)"
                            :disabled="props.readOnly"
                        ></vx-input>
                    </div>

                    <div>
                        <vx-label size="md">Country</vx-label>
                        <vx-selectable
                            data-test="country-input"
                            v-model="form.country"
                            :options="countries"
                            :reduce="(option) => option.value"
                            size="lg"
                            :error="displayFirstError(validate.country.$errors)"
                            :clearable="false"
                            :disabled="props.readOnly"
                        ></vx-selectable>
                    </div>

                    <div>
                        <vx-label size="md">City</vx-label>

                        <vx-input
                            data-test="city-input"
                            v-model="form.city"
                            size="lg"
                            :error="displayFirstError(validate.city.$errors)"
                            :disabled="props.readOnly"
                        ></vx-input>
                    </div>

                    <div>
                        <vx-label size="md">State</vx-label>

                        <vx-selectable
                            v-if="['US', 'CA'].includes(form.country)"
                            data-test="state-input"
                            v-model="form.state"
                            :options="states[form.country] ?? []"
                            :reduce="(option) => option.value"
                            size="lg"
                            :error="displayFirstError(validate.state.$errors)"
                            :clearable="false"
                            :disabled="props.readOnly"
                        ></vx-selectable>
                        <vx-input
                            v-else
                            data-test="state-input"
                            v-model="form.state"
                            size="lg"
                            :error="displayFirstError(validate.state.$errors)"
                            :disabled="props.readOnly"
                        ></vx-input>
                    </div>

                    <div>
                        <vx-label size="md">Zip</vx-label>

                        <vx-input
                            data-test="zip-input"
                            v-model="form.zip"
                            size="lg"
                            :error="displayFirstError(validate.zip.$errors)"
                            :disabled="props.readOnly"
                        ></vx-input>
                    </div>

                    <div>
                        <vx-label size="md" class="w-full">
                            <div class="flex items-center justify-between grow">
                                <span>Purchase Order Number</span>
                                <vx-badge color="slate" size="xs">
                                    <span class="pb-[1.5px] text-xs leading-snug">Optional</span>
                                    <font-awesome-icon widthAuto :icon="faPencilSlash" size="xs" />
                                </vx-badge>
                            </div>
                        </vx-label>

                        <vx-input
                            data-test="po-number-input"
                            v-model="form.po_number"
                            size="lg"
                            :error="displayFirstError(validate.po_number.$errors)"
                            :disabled="props.readOnly"
                        ></vx-input>
                    </div>
                </slot>

                <slot v-if="!props.readOnly" name="actions"></slot>
            </div>
        </template>
    </vx-expandable-card>
</template>
<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPencilSlash } from '@fortawesome/pro-solid-svg-icons';
import useVuelidate from '@vuelidate/core';
import { alphaNum, helpers, maxLength, minLength, numeric, required } from '@vuelidate/validators';
import { VxBadge, VxExpandableCard, VxInput, VxLabel, VxSelectable } from '@voxie/frontend-components';
import { displayFirstError } from '~/utils/validation';
import { countries, states } from '~/constants/countries';
import { computed, watch } from 'vue';

const props = defineProps({
    showAddressFields: {
        type: Boolean,
        default: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    }
});

const form = defineModel({
    type: Object,
    required: true,
});

const rules = computed(() => {
    if (!props.showAddressFields) {
        return {};
    }

    const defaultRules = {
        company_legal_name: { maxLength: maxLength(100) },
        address: { required: required, maxLength: maxLength(100) },
        address_line_2: { maxLength: maxLength(100) },
        city: { required: required, maxLength: maxLength(50) },
        state: { required: required, maxLength: maxLength(50) },
        country: { required: required, maxLength: maxLength(2) },
        zip: {
            required: required,
            alphaNum: helpers.withMessage('The ZIP code must be alpha-numeric', alphaNum),
            maxLength: maxLength(25),
        },
        po_number: {
            maxLength: maxLength(40),
            alphaNumDashUnderscore: helpers.withMessage(
                'The PO number must only contain letters, numbers, hyphens, underscores, and spaces',
                helpers.regex(/^[a-zA-Z0-9\-_\s]*$/)
            ),
        },
    };

    if (['US', 'CA'].includes(form.value.country)) {
        defaultRules.state.maxLength = maxLength(2);

        const zipCodeLength = form.value.country === 'CA' ? 6 : 5;
        defaultRules.zip = {
            ...defaultRules.zip,
            minLength: minLength(zipCodeLength),
            maxLength: maxLength(zipCodeLength),
        };

        if (form.value.country === 'US') {
            delete defaultRules.zip.alphaNum;
            defaultRules.zip.numeric = helpers.withMessage('The ZIP code must only contain numbers', numeric);
        }
    }

    return defaultRules;
});

const validate = useVuelidate(rules, form);

watch(
    () => form.value.country,
    () => {
        form.value.city = '';
        form.value.state = '';
        form.value.zip = '';
    }
);

defineExpose({
    validate,
});
</script>
